<template>
    <collapse-transition>
        <div :class="nodeClasses">
            <!-- tree-table-row -->
            <div v-if="columns.length > 0"
                :class="rowClasses"
                :style="rowStyle"
                @mouseenter.stop="handleMouseIn"
                @mouseleave.stop="handleMouseOut"
                @click="handleRowClick($event)">
                <!-- tree-table-cell -->
                <template v-for="(col, i) in columns">
                    <template v-if="isVisibile(col)">
                        <!-- 带缩进的单元格 -->
                        <tree-table-cell v-if="col.indent"
                            ref="indentCell"
                            :key="i"
                            :column="col"
                            :data="data"
                            :node="node"
                            :validation="getValidation(col.key)"
                            @click.native="handleExpand($event)">
                            <template slot="prefix">
                                <span :class="arrowClasses"
                                    :style="{marginLeft: (level - 1) * 18 + 'px'}">
                                    <Icon ref="arrow"
                                        v-if="showArrow"
                                        type="ios-arrow-forward"></Icon>
                                    <Icon v-if="showLoading"
                                        type="ios-loading"
                                        class="ivu-load-loop"></Icon>
                                </span>
                                <!-- Checkbox -->
                                <Checkbox v-if="showCheckbox"
                                    :value="node._isChecked"
                                    :indeterminate="node._isIndeterminate"
                                    :disabled="node._isDisabled || node._isDisableCheckbox"
                                    @click.native.prevent="handleCheck">
                                </Checkbox>
                            </template>
                        </tree-table-cell>
                        <!-- 其它单元格 -->
                        <tree-table-cell v-else
                            :key="i"
                            :column="col"
                            :data="data"
                            :node="node"
                            :validation="getValidation(col.key)">
                        </tree-table-cell>
                    </template>
                    <div v-else
                        :class="[prefixCls + '-cell', 'is-hidden']"
                        :style="{ width: col.width + 'px'}">
                    </div>
                </template>
            </div>
            <div :class="[prefixCls + '-row']">
                <!-- 子节点 -->
                <tree-table-node v-if="node._isExpand"
                    v-for="(cnode, i) in children"
                    :key="i"
                    :data="cnode.data"
                    :columns="columns"
                    :node="cnode"
                    :multiple="multiple"
                    :show-checkbox="showCheckbox"
                    :children-key="childrenKey"
                    :level="level + 1"
                    :location="location"
                    :validations="getValidations(cnode.nodeKey)">
                </tree-table-node>
            </div>
        </div>
    </collapse-transition>
</template>
<script>
import Checkbox from '../checkbox'
import Icon from '../icon'
import CollapseTransition from '../base/collapse-transition'
import { findComponentUpward, type } from '../../utils/assist'
import Emitter from '../../mixins/emitter'
import TreeTableCell from './tree-table-cell'

export default {
    name: 'TreeTableNode',
    mixins: [Emitter],
    components: {
        Checkbox,
        Icon,
        CollapseTransition,
        TreeTableCell,
    },
    inject: {
        treeRoot: { default: null }
    },
    props: {
        data: {
            type: Object,
            default () {
                return {}
            }
        },
        columns: {
            type: Array,
            default: function() {
                return []
            }
        },
        node: {
            type: Object,
        },
        multiple: {
            type: Boolean,
            default: false
        },
        childrenKey: {
            type: String,
            default: 'children'
        },
        showCheckbox: {
            type: Boolean,
            default: false
        },
        level: {
            type: Number,
            default: 1
        },
        location: {
            type: String,
            default: "middle",
            validator: val => ["left", "middle", "right"].indexOf(val) >= 0
        },
        validations: {
            type: Object,
            default: () => []
        }
    },

    data() {
        return {
            dragList: [],
        }
    },

    computed: {
        prefixCls() {
            return "ivu-tree-table"
        },

        nodeClasses() {
            return [
                `${this.prefixCls}-node`
            ]
        },

        rowClasses() {
            return [
                this.prefixCls + '-row',
                this.rowClassName,
                {
                    [`is-highlight`]: this.isHighlight,
                    "is-hover": this.isHover
                }
            ]
        },

        arrowClasses() {
            return [
                `${this.prefixCls}-arrow`,
                {
                    [`${this.prefixCls}-arrow-disabled`]: this.node._isDisabled,
                    [`${this.prefixCls}-arrow-open`]: this.node._isExpand
                }
            ]
        },

        rowClassName() {
            return this.treeRoot.rowClassName
        },

        rowStyle() {
            if (this.isHover || this.isHighlight) {
                return {}
            }

            var background = this.treeRoot.background,
                color

            if (type(background) == 'array') {
                color = background[this.node.level % background.length]
            }

            if (type(background) == 'function') {
                color = background(this.node)
            }

            return {
                "background-color": color || "#FFFFFF"
            }
        },

        showArrow() {
            return (this.data[this.childrenKey] && this.data[this.childrenKey].length) ||
                (typeof this.node._isLoading != "undefined")
        },

        showLoading() {
            return this.node._isLoading
        },

        isOdd() {
            return this.data.nodeKey % 2
        },

        isHover() {
            return !!this.node._isHover
        },

        isHighlight() {
            return !!this.node._isHighlight
        },

        children(){
            return this.node.children
        },
    },

    methods: {
        handleExpand(e) {
            var item = this.data
            
            if (this.node._isDisabled) return

            // 是否响应折叠
            var target = e.target,
                current = e.currentTarget,
                expander = this.treeRoot.expander,
                result = false

            // 排除checkbox的单击
            if (target.getAttribute("type") == "checkbox") {
                return
            }

            if (expander == "row") {
                result = true
            }

            if (expander == "cell") {
                result = target == this.$refs.indentCell[0].$el || this.$refs.indentCell[0].$el.contains(target)
            }

            if (expander == "icon") {
                result = target == this.$refs.arrow.$el
            }

            if (typeof expander == "function") {
                result = expander(this.$refs.indentCell[0].$el, this.$refs.arrow.$el, item)
            }

            // 不响应Expand
            if (result == false) {
                return
            }

            // async loading
            if (this.treeRoot.loadData && item[this.childrenKey].length === 0) {
                const treeTable = this.treeRoot

                this.node._isLoading = true
                treeTable.loadData(item, children => {
                    // children是异步取到的数据
                    this.node._isLoading = false

                    if (children.length) {
                        // 会触发重新构建，可能存在性能问题todo
                        this.$set(this.data, this.childrenKey, children)
                        // 为了在异步加载完毕后触发expand
                        this.$nextTick(() => this.handleExpand(e))
                    }
                })

                return
            }

            // 触发expand
            if (item[this.childrenKey] && item[this.childrenKey].length) {
                this.node._isExpand = !this.node._isExpand
                this.treeRoot.handleExpand(this.node)
            }
        },

        handleCheck() {
            if (this.node._isDisabled) return

            const changes = {
                _isChecked: !this.node._isChecked && !this.node._isIndeterminate,
                nodeKey: this.data.nodeKey
            }

            this.treeRoot.handleCheck(changes)
        },

        handleRowClick() {
            this.treeRoot.handleRowClick(this.node)

            // handleSelect
            if (this.node._isDisabled == false) {
                this.treeRoot.handleSelect(this.data.nodeKey)
            }
        },

        handleMouseIn() {
            this.node._isHover = true
        },

        handleMouseOut() {
            this.node._isHover = false
        },

        isVisibile(column) {
            return column.fixed == this.location
        },

        getNode(nodeKey) {
            return this.treeRoot.flatState[nodeKey]
        },

        getValidations(nodeKey) {
            return this.treeRoot.validationMap[nodeKey] || {}
        },

        getValidation(key) {
            return this.validations[key]
        },
    },

    watch: {
        data: {
            handler(newVal) {
                // 更新追踪状态
                this.treeRoot.saveTrack(newVal)
            },
            deep: true
        }
    }
}
</script>